/**
 * Audit log (owner product review §36) and service health. Primary label is human Persian; the raw
 * code is secondary. A row opens a drawer with the full technical metadata. Rows are immutable
 * (append-only in the database); this page only reads.
 */
import {
  AppContainer,
  Badge,
  Button,
  Card,
  DataTable,
  Drawer,
  KeyValueGrid,
  PageHeader,
  FilterChips,
  SectionHeader,
  StateView,
  TextField,
} from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  api,
  dt,
  errorMessage,
  type AuditRow,
  type ProviderHealth,
  type ProviderLane,
} from '../api.ts';
import { ACTOR, auditLabel, TARGET } from '../lib/labels.ts';
import { TechCode } from '../lib/ui.tsx';
import { Remote, useRemote } from '../lib/remote.tsx';

function Json({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-fg-subtle">—</span>;
  return (
    <pre
      dir="ltr"
      className="m-0 max-h-56 overflow-auto rounded-sm border border-line bg-surface-1 p-2.5 font-mono text-[11.5px] leading-5 text-fg"
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

const ACTION_GROUPS = [
  { id: '', label: 'همه' },
  { id: 'ADMIN_', label: 'اقدام‌های مدیر' },
  { id: 'admin.', label: 'دسترسی‌ها و جستجوی مدیر' },
  { id: 'identity.', label: 'هویت و نشست' },
  { id: 'kyc', label: 'احراز هویت' },
  { id: 'support.', label: 'پشتیبانی (کاربر)' },
  { id: 'notify.', label: 'اعلان و تلگرام' },
  { id: 'referral.', label: 'دعوت' },
];

type AuditFilter = { action: string; actorType: string; targetId: string; correlationId: string };
const query = (f: AuditFilter, before?: string) => {
  const p = new URLSearchParams();
  if (f.action) p.set('action', f.action);
  if (f.actorType) p.set('actorType', f.actorType);
  if (f.targetId.trim()) p.set('targetId', f.targetId.trim());
  if (f.correlationId.trim()) p.set('correlationId', f.correlationId.trim());
  if (before) p.set('before', before);
  const s = p.toString();
  return `/audit/search${s ? `?${s}` : ''}`;
};

export function AuditPage() {
  const [filter, setFilter] = useState<AuditFilter>({
    action: '',
    actorType: '',
    targetId: '',
    correlationId: '',
  });
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<AuditRow | null>(null);

  async function load(before?: string) {
    setBusy(true);
    try {
      const r = await api.get<{ items: AuditRow[] }>(query(filter, before));
      setRows((x) => (before && x ? [...x, ...r.items] : r.items));
      setMore(r.items.length >= 100);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    let live = true;
    api.get<{ items: AuditRow[] }>(query(filter)).then(
      (r) => {
        if (!live) return;
        setRows(r.items);
        setMore(r.items.length >= 100);
      },
      (e: unknown) => live && setError(errorMessage(e)),
    );
    return () => {
      live = false;
    };
  }, [filter]);

  return (
    <AppContainer>
      <PageHeader
        title="گزارش رویدادها"
        description="رد ممیزی تغییرناپذیر اقدام‌های کاربر، مدیر و سیستم. ردیف‌ها ویرایش یا حذف نمی‌شوند."
      />
      <AuditFilters
        value={filter}
        onChange={(f) => {
          setRows(null);
          setFilter(f);
        }}
      />
      {error ? (
        <Card className="p-6">
          <StateView
            kind="error"
            title="دریافت رویدادها ممکن نشد"
            description={error}
            action={<Button onPress={() => void load()}>تلاش دوباره</Button>}
          />
        </Card>
      ) : (
        <>
          <DataTable<AuditRow>
            label="رد ممیزی"
            rows={rows ?? []}
            loading={rows === null}
            density="compact"
            stickyFirst
            maxHeight="calc(100dvh - 260px)"
            onRowAction={(id) => setOpen(rows?.find((r) => r.id === id) ?? null)}
            columns={[
              {
                id: 'action',
                header: 'رویداد',
                isRowHeader: true,
                minWidth: '15rem',
                cell: (a) => (
                  <span className="flex flex-col" data-audit-row>
                    <span className="font-semibold">{auditLabel(a.action)}</span>
                    <bdi dir="ltr" className="font-mono text-[11px] text-fg-subtle">
                      {a.action}
                    </bdi>
                  </span>
                ),
              },
              {
                id: 'actor',
                header: 'انجام‌دهنده',
                cell: (a) => (
                  <span className="flex flex-col items-start gap-0.5">
                    <Badge tone={a.actorType === 'admin' ? 'accent' : 'neutral'}>
                      {ACTOR(a.actorType)}
                    </Badge>
                    {a.actorName && (
                      <bdi dir="ltr" className="font-mono text-[11px]">
                        {a.actorName}
                      </bdi>
                    )}
                  </span>
                ),
              },
              {
                id: 'target',
                header: 'هدف',
                cell: (a) =>
                  a.targetType === 'user' && a.targetId ? (
                    <Link to="/users/$id" params={{ id: a.targetId }} className="hover:underline">
                      پرونده‌ی کاربر
                    </Link>
                  ) : a.targetType ? (
                    (TARGET[a.targetType] ?? 'هدف ثبت‌شده')
                  ) : (
                    '—'
                  ),
              },
              { id: 'reason', header: 'دلیل', cell: (a) => a.reason ?? '—' },
              {
                id: 'ip',
                header: 'IP',
                cell: (a) => (
                  <bdi dir="ltr" className="font-mono text-[12px]">
                    {a.ipPrefix ?? '—'}
                  </bdi>
                ),
              },
              { id: 'at', header: 'زمان', cell: (a) => dt(a.at) },
            ]}
            empty={<StateView compact kind="empty" title="رویدادی ثبت نشده است" />}
          />
          {rows && more && (
            <div className="mt-3 flex justify-center">
              <Button
                variant="secondary"
                size="sm"
                loading={busy}
                onPress={() => void load(rows[rows.length - 1]?.at)}
              >
                رویدادهای قدیمی‌تر
              </Button>
            </div>
          )}
        </>
      )}
      {open && (
        <Drawer isOpen onOpenChange={(o) => !o && setOpen(null)} title={auditLabel(open.action)}>
          <div className="flex flex-col gap-4 text-fg" data-audit-detail>
            <KeyValueGrid
              columns={1}
              items={[
                {
                  label: 'کد رویداد',
                  value: (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {open.action}
                    </bdi>
                  ),
                },
                { label: 'زمان', value: dt(open.at) },
                {
                  label: 'زمان دقیق (UTC)',
                  value: (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {open.at}
                    </bdi>
                  ),
                },
                {
                  label: 'انجام‌دهنده',
                  value: `${ACTOR(open.actorType)}${open.actorId ? '' : ''}`,
                },
                {
                  label: 'شناسه‌ی انجام‌دهنده',
                  value: (
                    <bdi dir="ltr" className="font-mono text-[12px] break-all">
                      {open.actorId ?? '—'}
                    </bdi>
                  ),
                },
                {
                  label: 'هدف',
                  value: (
                    <bdi dir="ltr" className="font-mono text-[12px] break-all">
                      {open.targetType ? `${open.targetType}:${open.targetId ?? ''}` : '—'}
                    </bdi>
                  ),
                },
                { label: 'دلیل', value: open.reason ?? '—' },
                {
                  label: 'IP',
                  value: (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {open.ipPrefix ?? '—'}
                    </bdi>
                  ),
                },
                {
                  label: 'شناسه‌ی پیگیری',
                  value: (
                    <bdi dir="ltr" className="font-mono text-[12px] break-all">
                      {open.correlationId ?? '—'}
                    </bdi>
                  ),
                },
              ]}
            />
            <div>
              <p className="m-0 mb-1 text-[12px] text-fg-muted">وضعیت قبل</p>
              <Json value={open.before} />
            </div>
            <div>
              <p className="m-0 mb-1 text-[12px] text-fg-muted">وضعیت بعد</p>
              <Json value={open.after} />
            </div>
          </div>
        </Drawer>
      )}
    </AppContainer>
  );
}

/** Filters (server-side, exact): action group, actor type, target id, correlation id. */
function AuditFilters({
  value,
  onChange,
}: {
  value: AuditFilter;
  onChange: (f: AuditFilter) => void;
}) {
  const [target, setTarget] = useState(value.targetId);
  const [corr, setCorr] = useState(value.correlationId);
  return (
    <div className="mb-4 flex flex-col gap-3" data-audit-filters>
      <FilterChips
        label="گروه رویداد"
        options={ACTION_GROUPS}
        value={value.action}
        onChange={(action) => onChange({ ...value, action })}
      />
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          onChange({ ...value, targetId: target, correlationId: corr });
        }}
      >
        <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
          انجام‌دهنده
          <select
            value={value.actorType}
            onChange={(e) => onChange({ ...value, actorType: e.target.value })}
            className="h-10 rounded-md border border-line-strong bg-surface-1 px-3 text-[13px] font-normal text-fg"
          >
            <option value="">همه</option>
            <option value="admin">مدیر</option>
            <option value="user">کاربر</option>
            <option value="system">سیستم</option>
          </select>
        </label>
        <TextField label="شناسه‌ی هدف (دقیق)" value={target} onChange={setTarget} ltr />
        <TextField label="شناسه‌ی پیگیری (دقیق)" value={corr} onChange={setCorr} ltr />
        <Button type="submit" variant="secondary" size="sm">
          اعمال
        </Button>
      </form>
    </div>
  );
}

const LANE_LABEL: Record<ProviderLane, string> = {
  sms: 'پیامک',
  phoneOwnership: 'تطبیق مالکیت موبایل با کد ملی',
  civilIdentity: 'استعلام هویت (ثبت احوال)',
  bankIdentity: 'مالکیت کارت و شبا',
};
/** Breaker state is SECONDARY: on a simulator «سالم» says nothing about a live provider. */
const BREAKER: Record<string, string> = {
  CLOSED: 'مدارشکن بسته (بدون خطای اخیر)',
  OPEN: 'مدارشکن باز (قطع موقت)',
  HALF_OPEN: 'مدارشکن در حال آزمایش',
};

export function ProvidersPage() {
  const r = useRemote<ProviderHealth>('/providers/health');
  return (
    <AppContainer>
      <PageHeader
        title="سلامت سرویس‌ها"
        description="محیط هر سرویس (شبیه‌ساز یا ارائه‌دهنده‌ی واقعی) و وضعیت مدارشکن آن. محیط تولید بدون ارائه‌دهنده‌ی واقعی راه‌اندازی نمی‌شود."
      />
      <Remote r={r}>
        {(d) => (
          <div className="grid gap-4 md:grid-cols-2" data-provider-health>
            {(Object.keys(LANE_LABEL) as ProviderLane[]).map((lane) => (
              <Card key={lane} className="p-5" aria-labelledby={`lane-${lane}`}>
                <div className="flex items-center justify-between gap-2">
                  <SectionHeader id={`lane-${lane}`} title={LANE_LABEL[lane]} />
                  {(d.simulated?.[lane] ?? true) ? (
                    <Badge tone="accent">شبیه‌ساز · محیط آزمایشی</Badge>
                  ) : (
                    <Badge tone="positive">ارائه‌دهنده‌ی واقعی</Badge>
                  )}
                </div>
                <ul className="m-0 list-none divide-y divide-line p-0">
                  {Object.entries(d[lane]).map(([provider, state]) => (
                    <li
                      key={provider}
                      className="flex items-center justify-between gap-2 py-2.5 text-[13px]"
                      data-lane-mode={(d.simulated?.[lane] ?? true) ? 'simulator' : 'live'}
                    >
                      <span className="flex flex-col">
                        <span>{(d.simulated?.[lane] ?? true) ? 'شبیه‌ساز' : 'ارائه‌دهنده'}</span>
                        <TechCode>{provider}</TechCode>
                      </span>
                      <span className="text-[12px] text-fg-muted">{BREAKER[state] ?? state}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}
